import { useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Info, Lightbulb } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { useAjudaPapel } from "@/core/hooks/useAjudaPapel";
import { useProgressoAjuda } from "@/core/hooks/useProgressoAjuda";
import {
  destinoAnterior,
  indicePasso,
  montarLicoes,
  proximoDestino,
  type Destino,
} from "@/core/ajuda/licoes";
import { Ilustracao } from "@/core/components/ajuda/Ilustracao";
import {
  Aviso,
  BTN_PRIMARIO,
  BTN_SECUNDARIO,
  BTN_TEXTO,
  BarraProgresso,
  PAGINA_AJUDA,
  TextoAjuda,
} from "@/core/components/ajuda/ajudaUi";

function caminhoDoDestino(d: Destino): string {
  return d.tipo === "fim" ? "/ajuda/concluida" : `/ajuda/${d.licaoId}?passo=${d.passo + 1}`;
}

/**
 * Uma lição da Ajuda do Elos, um passo por vez. O passo atual fica na URL
 * (`?passo=2`): o botão voltar do aparelho e o atualizar a página funcionam.
 * Os passos mostrados dependem do que o Elos liberou para a pessoa e do papel dela
 * (quem lidera, quem gerencia um elo).
 */
export function AjudaLicao() {
  const { licaoId = "" } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { modulosVisiveis } = useAuth();
  const { papel, pronto } = useAjudaPapel();
  const { concluirLicao } = useProgressoAjuda();
  const tituloRef = useRef<HTMLHeadingElement>(null);

  const licoes = useMemo(() => montarLicoes(modulosVisiveis, papel), [modulosVisiveis, papel]);
  const indiceLicao = licoes.findIndex((l) => l.id === licaoId);
  const licao = licoes[indiceLicao];
  const passo = licao ? indicePasso(search.get("passo"), licao.passos.length) : 0;

  // A cada passo novo, o foco vai para o título e a tela volta ao topo: quem usa
  // leitor de tela ouve o passo novo, e quem usa o dedo não fica no meio da página.
  useEffect(() => {
    tituloRef.current?.focus();
    window.scrollTo(0, 0);
  }, [licaoId, passo]);

  const dado = licao?.passos[passo];
  // Lição de quem lidera ou gerencia, aberta direto pelo endereço: espera o papel carregar em vez
  // de mandar a pessoa para a lista.
  if (!licao && !pronto) {
    return (
      <main className={PAGINA_AJUDA}>
        <p role="status" className="text-base text-muted">
          Carregando a lição…
        </p>
      </main>
    );
  }
  if (!licao || !dado) return <Navigate to="/ajuda" replace />;

  const ultimoPasso = passo === licao.passos.length - 1;
  const ultimaLicao = indiceLicao === licoes.length - 1;
  const anterior = destinoAnterior(licoes, licao.id, passo);
  const total = licao.passos.length;
  const percentual = ((indiceLicao + (passo + 1) / total) / licoes.length) * 100;

  function avancar() {
    if (!licao) return;
    if (ultimoPasso) concluirLicao(licao.id);
    navigate(caminhoDoDestino(proximoDestino(licoes, licao.id, passo)));
  }

  return (
    <main className={PAGINA_AJUDA}>
      <BarraProgresso
        rotulo={`Lição ${indiceLicao + 1} de ${licoes.length}`}
        percentual={percentual}
      />
      <p className="mb-1 text-base font-semibold text-muted">
        {licao.titulo} · Passo {passo + 1} de {total}
      </p>
      <h1
        ref={tituloRef}
        tabIndex={-1}
        className="mb-4 text-2xl font-black leading-tight tracking-tight text-text outline-none sm:text-3xl"
      >
        {dado.titulo}
      </h1>

      <Ilustracao id={dado.ilustracao} modulosVisiveis={modulosVisiveis} papel={papel} />

      <div className="mt-5 flex flex-col gap-3">
        {dado.texto.map((t) => (
          <TextoAjuda key={t} texto={t} />
        ))}
      </div>

      {dado.depende && <Aviso icone={<Info size={20} aria-hidden="true" />}>{dado.depende}</Aviso>}
      {dado.dica && (
        <Aviso icone={<Lightbulb size={20} aria-hidden="true" />}>
          <TextoAjuda texto={dado.dica} className="!text-base" />
        </Aviso>
      )}

      <div className="mt-8 flex flex-col gap-3">
        <button type="button" onClick={avancar} className={BTN_PRIMARIO}>
          {ultimoPasso ? (ultimaLicao ? "Concluir" : "Concluir e continuar") : "Próximo"}
          <ArrowRight size={20} aria-hidden="true" />
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            disabled={!anterior}
            onClick={() => anterior && navigate(caminhoDoDestino(anterior))}
            className={BTN_SECUNDARIO}
          >
            <ArrowLeft size={18} aria-hidden="true" />
            Anterior
          </button>
          <button type="button" onClick={() => navigate("/ajuda")} className={BTN_TEXTO}>
            Lista de lições
          </button>
        </div>
        <button type="button" onClick={() => navigate("/inicio")} className={BTN_TEXTO}>
          Sair da Ajuda
        </button>
      </div>
    </main>
  );
}
